import { useViewRulesStore } from './stores/viewRulesStore';
import { PRIMARY_FILTER_ID, type LabelPosition, type ViewCondition, type DisplayOptions, type ViewRuleKind } from './models/ViewRules';

/**
 * Shared apply/clear helpers for view-rule-based recipes. A recipe's
 * `apply` composes several lenses at once (e.g. heatmap + label); this
 * clears any prior recipe/lens rules first so recipes are mutually
 * exclusive, then stacks the recipe's specs. Domain-agnostic — every
 * domain recipe file declares specs and calls these.
 */
export interface RecipeLensSpec {
  kind: ViewRuleKind;
  field: string;
  name?: string;
  minColor?: string;
  maxColor?: string;
  minValue?: number;
  maxValue?: number;
  labelTemplate?: string;
  labelPosition?: LabelPosition;
  conditions?: Array<Omit<ViewCondition, 'id'>>;
  display?: Pick<DisplayOptions, 'colors' | 'scopeMode'>;
}

/** Remove every lens/display rule except the primary filter. */
export function clearRecipeLenses(): void {
  const store = useViewRulesStore.getState();
  store.rules
    .filter((rule) => rule.id !== PRIMARY_FILTER_ID)
    .forEach((rule) => store.removeRule(rule.id));
}

/** Clear existing lenses, then apply the recipe's composite specs. */
export function applyRecipeLenses(specs: readonly RecipeLensSpec[]): void {
  clearRecipeLenses();
  const store = useViewRulesStore.getState();
  for (const spec of specs) {
    const ruleId = store.addViewRule(spec.kind, {
      field: spec.field,
      name: spec.name,
      minColor: spec.minColor,
      maxColor: spec.maxColor,
    });
    for (const condition of spec.conditions ?? []) store.addConditionToRule(ruleId, condition);
    if (spec.display) store.updateRuleDisplay(ruleId, spec.display);
    if (spec.kind === 'heatmap' && (spec.minValue != null || spec.maxValue != null)) {
      const rule = useViewRulesStore.getState().rules.find((item) => item.id === ruleId);
      const heatmap = rule?.display.heatmap;
      if (heatmap) {
        store.updateRuleDisplay(ruleId, {
          heatmap: {
            ...heatmap,
            ...(spec.minValue != null ? { minValue: spec.minValue } : {}),
            ...(spec.maxValue != null ? { maxValue: spec.maxValue } : {}),
          },
        });
      }
    }
    if (spec.kind === 'label') {
      store.updateRuleDisplay(ruleId, {
        label: {
          template: spec.labelTemplate ?? `{{${spec.field}}}`,
          position: spec.labelPosition ?? 'below',
        },
      });
    }
  }
}
